import { useState, useEffect, useMemo, type JSX } from 'react';
import { X } from 'lucide-react';
import clsx from 'clsx';
import ArbiSelect from '../ui/ArbiSelect';
import type { POActualCost, POCostType, POCostAllocationMethod } from '../../types';

/**
 * ActualCostsModal — Day 56 items 5 + 9.
 *
 * Captures actual cost entries logged after a PO becomes a real-world
 * commitment (Ordered → Received). Two scopes:
 *
 *   - PO-level: one row, allocation_method required (split_by_qty /
 *     split_by_cost / manual). Apportioned to lines at READ time on the
 *     variance card. 'manual' is an audit-trail marker — variance card shows
 *     the lump sum in the PO total but does NOT derive per-line splits from
 *     it. Operators wanting per-line precision use Per-line scope directly.
 *
 *   - Per-line: multi-select line items. One row per selected line at the
 *     entered amount each. line_item_id IS NOT NULL → the row IS the
 *     allocation; allocation_method stays NULL.
 *
 * Edit mode (`existingCost` prop set): pre-fills fields, button label flips
 * Save → Update. Delete handled by the caller via ConfirmDialog (this modal
 * does not own delete UI).
 */

const COST_TYPE_OPTIONS: { value: POCostType; label: string }[] = [
  { value: 'freight', label: 'Freight' },
  { value: 'handling', label: 'Handling' },
  { value: 'prep', label: 'Prep' },
  { value: 'storage', label: 'Storage' },
  { value: 'duty', label: 'Duty' },
  { value: 'tariff', label: 'Tariff' },
  { value: 'other', label: 'Other (specify below)' },
];

const ALLOCATION_OPTIONS: { value: POCostAllocationMethod; label: string }[] = [
  { value: 'split_by_qty', label: 'Split by quantity' },
  { value: 'split_by_cost', label: 'Split by cost' },
  { value: 'manual', label: 'Manual (audit only — no per-line split)' },
];

type Scope = 'po_level' | 'per_line';

export interface ActualCostFormPayload {
  scope: Scope;
  cost_type: POCostType;
  other_subtype: string | null;
  amount: number;
  allocation_method: POCostAllocationMethod | null;
  line_item_ids: string[]; // empty for PO-level
  notes: string | null;
}

interface ActualCostsModalLineItem {
  id: string;
  isg_sku: string | null;
  qty_ordered: number | null;
}

interface ActualCostsModalProps {
  open: boolean;
  poNumber: string;
  lineItems: ActualCostsModalLineItem[];
  existingCost?: POActualCost | null;
  onConfirm: (payload: ActualCostFormPayload) => void;
  onCancel: () => void;
}

export default function ActualCostsModal({
  open,
  poNumber,
  lineItems,
  existingCost,
  onConfirm,
  onCancel,
}: ActualCostsModalProps): JSX.Element | null {
  const isEdit = !!existingCost;

  const [scope, setScope] = useState<Scope>('po_level');
  const [costType, setCostType] = useState<POCostType | null>(null);
  const [otherSubtype, setOtherSubtype] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [allocationMethod, setAllocationMethod] = useState<POCostAllocationMethod | null>(null);
  const [selectedLineIds, setSelectedLineIds] = useState<string[]>([]);
  const [notes, setNotes] = useState<string>('');

  // Reset / pre-fill on open. Edit mode pulls from existingCost.
  useEffect(() => {
    if (!open) return;
    if (existingCost) {
      const isLineScope = !!existingCost.line_item_id;
      setScope(isLineScope ? 'per_line' : 'po_level');
      setCostType(existingCost.cost_type);
      setOtherSubtype(existingCost.other_subtype ?? '');
      setAmount(existingCost.amount != null ? String(existingCost.amount) : '');
      setAllocationMethod(existingCost.allocation_method ?? null);
      setSelectedLineIds(existingCost.line_item_id ? [existingCost.line_item_id] : []);
      setNotes(existingCost.notes ?? '');
    } else {
      setScope('po_level');
      setCostType(null);
      setOtherSubtype('');
      setAmount('');
      setAllocationMethod(null);
      setSelectedLineIds([]);
      setNotes('');
    }
  }, [open, existingCost]);

  // Escape closes
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  const amountValid = useMemo(() => {
    const n = Number(amount);
    return amount !== '' && !isNaN(n) && n >= 0;
  }, [amount]);

  const otherInvalid = costType === 'other' && otherSubtype.trim().length === 0;

  const submitDisabled =
    costType === null ||
    !amountValid ||
    otherInvalid ||
    (scope === 'po_level' && allocationMethod === null) ||
    (scope === 'per_line' && selectedLineIds.length === 0);

  if (!open) return null;

  const handleConfirm = (): void => {
    if (submitDisabled || costType === null) return;
    onConfirm({
      scope,
      cost_type: costType,
      other_subtype: costType === 'other' ? otherSubtype.trim() : null,
      amount: Number(amount),
      allocation_method: scope === 'po_level' ? allocationMethod : null,
      line_item_ids: scope === 'per_line' ? selectedLineIds : [],
      notes: notes.trim().length > 0 ? notes.trim() : null,
    });
  };

  const toggleLine = (id: string): void => {
    setSelectedLineIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Add actual cost"
      data-testid="actual-cost-modal"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onCancel}
        className="absolute inset-0 bg-black/50"
      />
      <div className="relative bg-surface-2 border border-border rounded-lg shadow-xl w-[480px] max-h-[80vh] overflow-y-auto p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-ink-bright">
              {isEdit ? 'Edit actual cost' : 'Add actual cost'}
            </h3>
            <p className="text-2xs text-ink-muted mt-0.5">PO {poNumber}</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close modal"
            className="text-ink-muted hover:text-ink-primary transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Scope toggle — locked in edit mode (changing scope mid-edit would
            require dropping/recreating the row). */}
        <div>
          <div className="text-2xs text-ink-muted font-medium mb-1">Scope</div>
          <div
            data-testid="actual-cost-scope"
            className="inline-flex rounded-md border border-border overflow-hidden"
          >
            {(
              [
                { v: 'po_level', label: 'PO-level' },
                { v: 'per_line', label: 'Per-line item' },
              ] as { v: Scope; label: string }[]
            ).map((opt) => (
              <button
                key={opt.v}
                type="button"
                disabled={isEdit}
                onClick={() => setScope(opt.v)}
                className={clsx(
                  'px-3 py-1 text-xs transition-colors',
                  scope === opt.v
                    ? 'bg-accent text-white'
                    : 'bg-surface-3 text-ink-secondary hover:text-ink-primary',
                  isEdit && 'opacity-50 cursor-not-allowed'
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {isEdit && (
            <p className="text-2xs text-ink-muted mt-1">
              Scope cannot change in edit mode — delete and re-create instead.
            </p>
          )}
        </div>

        <div>
          <div className="text-2xs text-ink-muted font-medium mb-1">Cost type</div>
          <div data-testid="actual-cost-type">
            <ArbiSelect<POCostType>
              value={costType ?? undefined}
              options={COST_TYPE_OPTIONS}
              onChange={(v) => setCostType(v)}
              placeholder="Select cost type…"
            />
          </div>
        </div>

        {costType === 'other' && (
          <div>
            <label
              htmlFor="actual-cost-other-subtype"
              className="text-2xs text-ink-muted font-medium mb-1 block"
            >
              Specify type <span className="text-danger">*</span>
            </label>
            <input
              id="actual-cost-other-subtype"
              data-testid="actual-cost-other-subtype"
              value={otherSubtype}
              onChange={(e) => setOtherSubtype(e.target.value)}
              className="arbi-input text-xs w-full"
              placeholder="e.g. expedited freight surcharge"
            />
            {otherInvalid && (
              <p className="text-2xs text-danger mt-1">Required when type is &quot;Other&quot;</p>
            )}
          </div>
        )}

        <div>
          <label
            htmlFor="actual-cost-amount"
            className="text-2xs text-ink-muted font-medium mb-1 block"
          >
            Amount{' '}
            {scope === 'per_line' && <span className="text-ink-muted">(per selected line)</span>}{' '}
            <span className="text-danger">*</span>
          </label>
          <div className="flex items-center gap-1">
            <span className="text-xs font-mono text-ink-muted">$</span>
            <input
              id="actual-cost-amount"
              data-testid="actual-cost-amount"
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="arbi-input text-xs font-mono w-32"
              placeholder="0.00"
            />
          </div>
        </div>

        {scope === 'po_level' && (
          <div>
            <div className="text-2xs text-ink-muted font-medium mb-1">
              Allocation method <span className="text-danger">*</span>
            </div>
            <div data-testid="actual-cost-allocation">
              <ArbiSelect<POCostAllocationMethod>
                value={allocationMethod ?? undefined}
                options={ALLOCATION_OPTIONS}
                onChange={(v) => setAllocationMethod(v)}
                placeholder="Select method…"
              />
            </div>
          </div>
        )}

        {scope === 'per_line' && (
          <div>
            <div className="text-2xs text-ink-muted font-medium mb-1">
              Apply to lines <span className="text-danger">*</span>
            </div>
            {lineItems.length === 0 ? (
              <p className="text-xs text-ink-muted">No line items on this PO</p>
            ) : (
              <div className="border border-border rounded-md max-h-48 overflow-y-auto bg-surface-3">
                {lineItems.map((li) => (
                  <label
                    key={li.id}
                    className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-surface-2"
                  >
                    <input
                      type="checkbox"
                      checked={selectedLineIds.includes(li.id)}
                      onChange={() => toggleLine(li.id)}
                      disabled={isEdit}
                      className="accent-accent"
                    />
                    <span className="font-mono text-ink-primary">
                      {li.isg_sku || li.id.slice(0, 8)}
                    </span>
                    <span className="text-ink-muted">·</span>
                    <span className="text-ink-secondary">qty {li.qty_ordered ?? '—'}</span>
                  </label>
                ))}
              </div>
            )}
            {isEdit && (
              <p className="text-2xs text-ink-muted mt-1">
                Line cannot change in edit mode — delete and re-create instead.
              </p>
            )}
          </div>
        )}

        <div>
          <label
            htmlFor="actual-cost-notes"
            className="text-2xs text-ink-muted font-medium mb-1 block"
          >
            Notes
          </label>
          <textarea
            id="actual-cost-notes"
            data-testid="actual-cost-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="arbi-input text-xs w-full resize-none"
            placeholder="Optional"
          />
        </div>

        <div className="flex items-center justify-end gap-2 pt-1 border-t border-border-subtle">
          <button
            type="button"
            onClick={onCancel}
            data-testid="actual-cost-cancel"
            className="arbi-btn text-xs bg-surface-4 text-ink-secondary hover:text-ink-bright hover:bg-surface-5 px-3 py-1.5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            data-testid="actual-cost-confirm"
            disabled={submitDisabled}
            className="arbi-btn text-xs bg-accent text-white hover:bg-accent-hover px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isEdit ? 'Update' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
